import { FC, ReactNode, useMemo } from 'react';
import {
  expectedKeyOrder,
  FontDescriptor,
  fontDescriptors,
  fontName,
  movedKeys
} from '../../helpers/fontChangesHelpers';
import { XrayAnalysisState } from '../../hooks/useXrayAnalysis';
import {
  FontCheckHowTo,
  FontDescriptorCard,
  FontDescriptorKey,
  FontDescriptorList,
  FontDescriptorName,
  FontDescriptorText,
  ListCheckEmptyState
} from '../../styles';
import { listCheckCopy } from './listCheckCopy';
import { messageOf, TextSide } from './TextPropertiesCompare';

interface AddedFontsTabProps {
  documentXray?: XrayAnalysisState;
}

const withKeys = (descriptor: FontDescriptor, moved: number[]): ReactNode[] => {
  const parts: ReactNode[] = [];
  let at = 0;
  descriptor.keys.forEach(({ start, end }, index) => {
    const isMoved = moved.includes(index);
    parts.push(descriptor.text.slice(at, start));
    parts.push(
      <FontDescriptorKey
        key={start}
        $moved={isMoved}
        data-testid={isMoved ? 'font-changes-moved-key' : undefined}
      >
        {descriptor.text.slice(start, end)}
      </FontDescriptorKey>
    );
    at = end;
  });
  parts.push(descriptor.text.slice(at));
  return parts;
};

const AddedFontsTab: FC<AddedFontsTabProps> = ({ documentXray }) => {
  const byteCode = documentXray?.data?.byte_code;
  const descriptors = useMemo(() => fontDescriptors(byteCode), [byteCode]);
  const expected = expectedKeyOrder(descriptors);
  const side: TextSide = {
    title: listCheckCopy.thisDocument,
    status: documentXray?.status ?? 'idle',
    data: documentXray?.data ?? null,
    onRetry: documentXray?.retry
  };
  const message = messageOf(side, !descriptors.length, listCheckCopy.fontChanges.noDescriptors);

  return (
    <div data-testid="font-changes-added-fonts">
      <FontCheckHowTo>{listCheckCopy.fontChanges.addedFontsHowTo}</FontCheckHowTo>
      {message ? (
        <ListCheckEmptyState>{message}</ListCheckEmptyState>
      ) : (
        <FontDescriptorList>
          {descriptors.map((descriptor) => {
            const moved = expected
              ? movedKeys(
                  descriptor.keys.map((k) => k.name),
                  expected
                )
              : [];
            const name = fontName(descriptor);
            return (
              <FontDescriptorCard key={descriptor.offset} $moved={moved.length > 0}>
                {name && (
                  <FontDescriptorName
                    $moved={moved.length > 0}
                    data-testid="font-changes-descriptor-name"
                  >
                    {name}
                  </FontDescriptorName>
                )}
                <FontDescriptorText
                  data-testid="font-changes-descriptor"
                  data-moved={moved.length > 0}
                >
                  {withKeys(descriptor, moved)}
                </FontDescriptorText>
              </FontDescriptorCard>
            );
          })}
        </FontDescriptorList>
      )}
    </div>
  );
};

export default AddedFontsTab;
